import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { theme } from "../theme";
import { SIGN_IN as C } from "../lib/signInPalette";
import { BlueprintBackdrop } from "../components/BlueprintBackdrop";
import { PasswordRules, passwordLooksAcceptable } from "../components/PasswordRules";

// "Forgot password" (ask for a link) and "reset password" (the link's landing
// page). Both are public. The reset token arrives in the link's fragment
// (#token=), which the browser never sends to a server or in a Referer; it is
// read here, taken out of the address bar and posted in the body. Links sent
// before the fragment was used carry ?token= and still work. A reset never
// touches the authenticator app.

const API = import.meta.env.VITE_API_BASE_URL as string;

// The shared dark sign-in palette (lib/signInPalette.ts).

const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: theme.radius.sm,
  border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.06)", color: C.heading, fontSize: 13, outline: "none",
};
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 };
const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  width: "100%", padding: "10px 18px", borderRadius: theme.radius.sm, border: "none", background: C.accent,
  color: "#fff", fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
});
const linkBtn: React.CSSProperties = { background: "none", border: "none", color: C.subtle, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 };

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: theme.font, padding: 16, position: "relative" }}>
      <BlueprintBackdrop surface="login" />
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 440 }}>
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: theme.radius.lg, padding: 24,
          boxShadow: "var(--c-sheetShadow)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.heading, marginBottom: 16 }}>{title}</div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy]   = useState(false);
  const [sent, setSent]   = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/auth/password/forgot`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 429) { setError("Too many requests. Please wait a few minutes and try again."); return; }
      if (!res.ok) { setError("Could not send the reset link. Please try again."); return; }
      setSent(true);
    } catch {
      setError("Could not send the reset link. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Reset your password">
      {sent ? (
        <>
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>
            If <strong>{email.trim()}</strong> has a password account, a reset link is on its way. It is valid for 30 minutes.
            Check your spam folder if it does not arrive.
          </div>
          <button type="button" onClick={() => navigate("/login")} style={linkBtn}>Back to sign in</button>
        </>
      ) : (
        <form onSubmit={submit}>
          <div style={{ fontSize: 12, color: C.subtle, lineHeight: 1.6, marginBottom: 16 }}>
            Enter the email address your account was invited on and we will send a link to choose a new password.
            You will still need your authenticator app to sign in afterwards.
          </div>
          <label style={labelStyle} htmlFor="forgot-email">Email address</label>
          <input id="forgot-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }} />
          {error && <div style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>{error}</div>}
          <button type="submit" disabled={busy || !email.trim()} style={primaryBtn(busy || !email.trim())}>{busy ? "Sending…" : "Send reset link"}</button>
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <button type="button" onClick={() => navigate("/login")} style={linkBtn}>Back to sign in</button>
          </div>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  // Read once on first render: the effect below clears it from the address bar.
  const [token] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).get("token")
    ?? new URLSearchParams(window.location.search).get("token")
    ?? "");
  useEffect(() => {
    // Out of the history entry, so it is not left in the address bar or the back stack.
    if (window.location.hash || window.location.search) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState("");

  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !!token && passwordLooksAcceptable(password) && confirm === password && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/auth/password/reset`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, newPassword: password }),
      });
      if (res.ok) { setDone(true); return; }
      const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
      if (body.error === "invalid_token") setError("This reset link is invalid or has expired. Request a new one.");
      else setError(body.message ?? "Could not reset the password. Please try again.");
    } catch {
      setError("Could not reset the password. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <Shell title="Reset your password">
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>This link is missing its reset token. Open the link from the email again, or request a new one.</div>
        <button type="button" onClick={() => navigate("/forgot-password")} style={linkBtn}>Request a new link</button>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Password changed">
        <div style={{ fontSize: 13, color: C.success, lineHeight: 1.6, marginBottom: 16 }}>
          Your new password is set and every other session has been signed out. Sign in with it and your authenticator app.
        </div>
        <button type="button" onClick={() => navigate("/login")} style={primaryBtn(false)}>Go to sign in</button>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit}>
        <label style={labelStyle} htmlFor="reset-password">New password</label>
        <input id="reset-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }} />
        <PasswordRules password={password} dark />
        <label style={{ ...labelStyle, marginTop: 14 }} htmlFor="reset-confirm">Confirm new password</label>
        <input id="reset-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }} />
        {mismatch && <div style={{ fontSize: 12, color: C.danger, marginBottom: 8 }}>The passwords do not match.</div>}
        {error && <div style={{ fontSize: 12, color: C.danger, marginBottom: 12 }}>{error}</div>}
        <button type="submit" disabled={!canSubmit} style={{ ...primaryBtn(!canSubmit), marginTop: 8 }}>{busy ? "Saving…" : "Set new password"}</button>
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button type="button" onClick={() => navigate("/login")} style={linkBtn}>Back to sign in</button>
        </div>
      </form>
    </Shell>
  );
}
